<template>
  <div class="page admin-page">
    <div class="container">
      <!-- Header -->
      <div class="header">
        <div class="header-left">
          <h1><span>🎬</span> 视频管理</h1>
          <p class="subtitle">管理案例视频和标准答案</p>
        </div>
        <div class="header-right">
          <span class="user-info">👤 {{ currentUser.realName || '加载中...' }}</span>
          <button class="logout-btn" @click="goBack">返回首页</button>
        </div>
      </div>
      
      <!-- Actions Bar -->
      <div class="actions-bar">
        <div class="actions-left">
          <button class="btn btn-primary" @click="showCreateForm = !showCreateForm">
            <span class="btn-icon">{{ showCreateForm ? '✕' : '➕' }}</span>
            {{ showCreateForm ? '取消创建' : '上传视频' }}
          </button>
          <select v-model="selectedCaseId" @change="loadVideos" class="select-control">
            <option :value="null" disabled>请选择案例</option>
            <option v-for="c in cases" :key="c.id" :value="c.id">{{ c.title }}</option>
          </select>
          <button class="btn btn-success" @click="loadVideos" :disabled="!selectedCaseId">
            <span class="btn-icon">🔄</span> 刷新列表
          </button>
        </div>
        <div class="actions-right">
          <span v-if="selectedCaseId" class="stats">
            共 {{ videos.length }} 个视频
          </span>
        </div>
      </div>
      
      <!-- Create Form -->
      <transition name="slide-fade">
        <div v-if="showCreateForm" class="form-card">
          <div class="form-header">
            <h3>📹 上传新视频</h3>
          </div>
          <div class="form-body">
            <div class="form-row">
              <div class="form-group">
                <label>视频文件</label>
                <input
                  ref="videoFileInput"
                  type="file"
                  accept="video/*"
                  class="form-control file-control"
                  @change="handleVideoFileChange"
                >
              </div>
              <div class="form-group">
                <label>所属案例</label>
                <select v-model="newVideo.caseId" class="form-control">
                  <option value="">请选择案例</option>
                  <option v-for="c in cases" :key="c.id" :value="c.id">{{ c.title }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>视频标题</label>
                <input v-model="newVideo.title" placeholder="请输入视频标题" class="form-control">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>视频类型</label>
                <select v-model="newVideo.videoType" class="form-control">
                  <option value="0">系统使用教程</option>
                  <option value="1">情景介绍</option>
                  <option value="2">考试内容</option>
                  <option value="3">复盘</option>
                </select>
              </div>
              <div class="form-group">
                <label>播放顺序</label>
                <input v-model="newVideo.sortOrder" type="number" placeholder="序号" class="form-control">
              </div>
              <div class="form-group">
                <label>触发时间(秒)</label>
                <input v-model="newVideo.triggerTime" type="number" placeholder="0" class="form-control">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group" style="flex: 1;">
                <label>视频描述</label>
                <textarea v-model="newVideo.description" placeholder="请输入视频描述（选填）" class="form-control textarea" rows="3"></textarea>
              </div>
            </div>
            <div class="form-actions">
              <button
                class="btn btn-primary"
                @click="uploadVideo"
                :disabled="uploading || !newVideo.file || !newVideo.caseId || !newVideo.title.trim()"
              >
                <span v-if="uploading" class="spinner-small"></span>
                {{ uploading ? '上传中...' : '✅ 确认上传' }}
              </button>
            </div>
          </div>
        </div>
      </transition>
      
      <!-- Video List -->
      <div class="video-grid">
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>加载中...</p>
        </div>
        
        <div v-else-if="!selectedCaseId" class="empty-state">
          <div class="empty-icon">📂</div>
          <h3>请选择案例</h3>
          <p>选择左侧案例以查看和管理视频</p>
        </div>
        
        <div v-else-if="videos.length === 0" class="empty-state">
          <div class="empty-icon">🎬</div>
          <h3>暂无视频</h3>
          <p>点击上方"上传视频"按钮开始添加</p>
        </div>
        
        <div v-else class="video-cards">
          <div v-for="item in videos" :key="item.id" class="video-card">
            <div class="video-card-header">
              <div class="video-type-badge" :class="getVideoTypeClass(item.videoType)">
                {{ getVideoTypeText(item.videoType) }}
              </div>
              <div class="video-actions">
                <button class="icon-btn" @click="editVideo(item)" title="编辑">✏️</button>
                <button class="icon-btn danger" @click="deleteVideo(item)" title="删除">🗑️</button>
              </div>
            </div>
            
            <div class="video-card-body">
              <h4 class="video-title">{{ item.title }}</h4>
              <div v-if="item.description" class="video-description">
                <p>{{ item.description }}</p>
              </div>
              <div class="video-info">
                <div class="info-item">
                  <span class="info-label">ID:</span>
                  <span class="info-value">{{ item.id }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">顺序:</span>
                  <span class="info-value">{{ item.sortOrder }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">触发:</span>
                  <span class="info-value">{{ item.triggerTime || 0 }}秒</span>
                </div>
              </div>
            </div>
            
            <div v-if="item.videoType !== 0" class="video-card-footer">
              <button class="btn btn-sm btn-outline" @click="toggleAnswerKeys(item)">
                <span class="btn-icon">{{ expandedVideoId === item.id ? '📖' : '📝' }}</span>
                {{ expandedVideoId === item.id ? '收起答案' : '管理答案' }}
                <span class="badge" v-if="getAnswerKeyCount(item.id) > 0">{{ getAnswerKeyCount(item.id) }}</span>
              </button>
            </div>
            
            <!-- Answer Keys Panel -->
            <transition name="slide-down">
              <div v-if="expandedVideoId === item.id" class="answer-panel">
                <div class="answer-header">
                  <h5>标准答案管理</h5>
                  <button class="btn btn-sm btn-primary" @click="showAddAnswerKey(item)">
                    ➕ 添加答案
                  </button>
                </div>
                
                <div v-if="loadingAnswerKeys[item.id]" class="loading-small">
                  <div class="spinner-small"></div>
                  <span>加载中...</span>
                </div>
                
                <div v-else-if="!answerKeys[item.id] || answerKeys[item.id].length === 0" class="empty-answer">
                  <p>暂无标准答案，点击上方按钮添加</p>
                </div>
                
                <div v-else class="answer-list">
                  <div v-for="(ak, index) in answerKeys[item.id]" :key="ak.id" class="answer-item">
                    <div class="answer-content">
                      <span class="answer-index">{{ index + 1 }}.</span>
                      <span class="answer-text">{{ ak.content }}</span>
                    </div>
                    <div class="answer-actions">
                      <button class="icon-btn small" @click="editAnswerKey(item.id, ak)" title="编辑">✏️</button>
                      <button class="icon-btn small danger" @click="deleteAnswerKey(item.id, ak)" title="删除">🗑️</button>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Answer Key Modal -->
    <div v-if="showAnswerKeyModal" class="modal-overlay" @click.self="closeAnswerKeyModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingAnswerKey ? '编辑标准答案' : '添加标准答案' }}</h3>
          <button class="close-btn" @click="closeAnswerKeyModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>答案内容</label>
            <textarea 
              v-model="answerKeyForm.content" 
              placeholder="请输入标准答案内容..." 
              class="form-control textarea"
              rows="4"
            ></textarea>
          </div>
          <div class="form-group">
            <label>排序</label>
            <input v-model="answerKeyForm.sortOrder" type="number" placeholder="0" class="form-control">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline" @click="closeAnswerKeyModal">取消</button>
          <button class="btn btn-primary" @click="saveAnswerKey" :disabled="saving">
            {{ saving ? '保存中...' : '确定' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Video Edit Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>编辑视频</h3>
          <button class="close-btn" @click="closeEditModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>视频标题</label>
            <input v-model="editingVideo.title" placeholder="请输入视频标题" class="form-control">
          </div>
          <div class="form-group">
            <label>视频描述</label>
            <textarea v-model="editingVideo.description" placeholder="请输入视频描述（选填）" class="form-control textarea" rows="3"></textarea>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>视频类型</label>
              <select v-model="editingVideo.videoType" class="form-control">
                <option :value="0">系统使用教程</option>
                <option :value="1">情景介绍</option>
                <option :value="2">考试内容</option>
                <option :value="3">复盘</option>
              </select>
            </div>
            <div class="form-group">
              <label>播放顺序</label>
              <input v-model="editingVideo.sortOrder" type="number" placeholder="序号" class="form-control">
            </div>
            <div class="form-group">
              <label>触发时间(秒)</label>
              <input v-model="editingVideo.triggerTime" type="number" placeholder="0" class="form-control">
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline" @click="closeEditModal">取消</button>
          <button class="btn btn-primary" @click="saveVideoEdit" :disabled="saving">
            {{ saving ? '保存中...' : '确定' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Videos',
  data() {
    return {
      currentUser: JSON.parse(localStorage.getItem('userInfo') || '{}'),
      cases: [],
      selectedCaseId: null,
      videos: [],
      newVideo: {
        file: null,
        caseId: '',
        title: '',
        description: '',
        videoType: '1',
        sortOrder: '0',
        triggerTime: '0'
      },
      showCreateForm: false,
      loading: false,
      uploading: false,
      
      // Answer key management
      expandedVideoId: null,
      answerKeys: {},
      loadingAnswerKeys: {},
      showAnswerKeyModal: false,
      editingAnswerKey: null,
      answerKeyForm: {
        content: '',
        sortOrder: 0
      },
      saving: false,
      currentVideoId: null,
      editingVideo: {},
      showEditModal: false
    }
  },
  mounted() {
    this.loadCases()
  },
  methods: {
    async loadCases() {
      try {
        const response = await axios.get('/api/cases')
        if (Array.isArray(response.data)) {
          this.cases = response.data
        } else if (response.data && response.data.code === 200) {
          this.cases = response.data.data || []
        }
      } catch (error) {
        console.error('加载案例失败:', error)
      }
    },
    
    async loadVideos() {
      if (!this.selectedCaseId) {
        alert('请先选择案例')
        return
      }
      this.loading = true
      try {
        const response = await axios.get(`/api/videos/case/${this.selectedCaseId}`)
        if (Array.isArray(response.data)) {
          this.videos = response.data
        } else if (response.data && response.data.code === 200) {
          this.videos = response.data.data || []
        }
      } catch (error) {
        console.error('加载视频失败:', error)
        alert('加载失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },
    
    async uploadVideo() {
      if (!this.newVideo.file || !this.newVideo.caseId || !this.newVideo.title.trim()) {
        alert('请选择视频文件、所属案例并填写视频标题')
        return
      }
      
      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('video', this.newVideo.file)
        formData.append('caseId', this.newVideo.caseId)
        formData.append('title', this.newVideo.title.trim())
        formData.append('description', this.newVideo.description || '')
        formData.append('videoType', this.newVideo.videoType)
        formData.append('sortOrder', this.newVideo.sortOrder || 0)

        const response = await axios.post('/api/videos/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        
        const uploadedVideo = response.data && response.data.code === 200
          ? response.data.data
          : response.data
        if (uploadedVideo && uploadedVideo.id) {
          alert('上传成功！')
          this.selectedCaseId = Number(this.newVideo.caseId)
          this.resetUploadForm()
          this.showCreateForm = false
          await this.loadVideos()
        } else {
          throw new Error('服务器未返回上传结果')
        }
      } catch (error) {
        console.error('上传视频失败:', error)
        alert('上传失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.uploading = false
      }
    },

    handleVideoFileChange(event) {
      this.newVideo.file = event.target.files && event.target.files[0]
        ? event.target.files[0]
        : null
    },

    resetUploadForm() {
      this.newVideo = {
        file: null,
        caseId: '',
        title: '',
        description: '',
        videoType: '1',
        sortOrder: '0',
        triggerTime: '0'
      }
      if (this.$refs.videoFileInput) {
        this.$refs.videoFileInput.value = ''
      }
    },
    
    getVideoTypeText(type) {
      const types = { 0: '系统使用教程', 1: '情景介绍', 2: '考试内容', 3: '复盘' }
      return types[type] || '未知'
    },
    
    getVideoTypeClass(type) {
      return {
        'type-tutorial': type === 0,
        'type-intro': type === 1,
        'type-exam': type === 2,
        'type-review': type === 3
      }
    },
    
    editVideo(video) {
      console.log('Editing video:', video)
      this.editingVideo = Object.assign({}, video)
      this.showEditModal = true
      console.log('EditingVideo after assign:', this.editingVideo)
      console.log('ShowEditModal:', this.showEditModal)
    },
    
    closeEditModal() {
      this.showEditModal = false
      this.editingVideo = {}
    },
    
    async saveVideoEdit() {
      if (!this.editingVideo.title || !this.editingVideo.title.trim()) {
        alert('请输入视频标题')
        return
      }
      
      this.saving = true
      try {
        await axios.put(`/api/videos/${this.editingVideo.id}`, {
          title: this.editingVideo.title,
          description: this.editingVideo.description,
          videoType: this.editingVideo.videoType,
          sortOrder: this.editingVideo.sortOrder,
          triggerTime: this.editingVideo.triggerTime
        })
        alert('保存成功！')
        this.closeEditModal()
        await this.loadVideos()
      } catch (error) {
        alert('保存失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.saving = false
      }
    },
    
    async deleteVideo(video) {
      if (!confirm(`确定要删除视频"${video.title}"吗？`)) return
      
      try {
        await axios.delete(`/api/videos/${video.id}`)
        alert('删除成功！')
        await this.loadVideos()
      } catch (error) {
        alert('删除失败：' + (error.response?.data?.message || error.message))
      }
    },
    
    // Answer key management
    async toggleAnswerKeys(video) {
      if (this.expandedVideoId === video.id) {
        this.expandedVideoId = null
      } else {
        this.expandedVideoId = video.id
        await this.loadAnswerKeys(video.id)
      }
    },
    
    async loadAnswerKeys(videoId) {
      this.$set(this.loadingAnswerKeys, videoId, true)
      try {
        const response = await axios.get(`/api/videos/${videoId}/answer-keys`)
        this.$set(this.answerKeys, videoId, response.data || [])
      } catch (error) {
        console.error('加载标准答案失败:', error)
      } finally {
        this.$set(this.loadingAnswerKeys, videoId, false)
      }
    },
    
    getAnswerKeyCount(videoId) {
      return this.answerKeys[videoId] ? this.answerKeys[videoId].length : 0
    },
    
    showAddAnswerKey(video) {
      this.currentVideoId = video.id
      this.editingAnswerKey = null
      this.answerKeyForm = { content: '', sortOrder: 0 }
      this.showAnswerKeyModal = true
    },
    
    editAnswerKey(videoId, answerKey) {
      this.currentVideoId = videoId
      this.editingAnswerKey = answerKey
      this.answerKeyForm = {
        content: answerKey.content,
        sortOrder: answerKey.sortOrder || 0
      }
      this.showAnswerKeyModal = true
    },
    
    closeAnswerKeyModal() {
      this.showAnswerKeyModal = false
      this.editingAnswerKey = null
      this.answerKeyForm = { content: '', sortOrder: 0 }
    },
    
    async saveAnswerKey() {
      if (!this.answerKeyForm.content.trim()) {
        alert('请输入答案内容')
        return
      }
      
      this.saving = true
      try {
        if (this.editingAnswerKey) {
          // Update
          await axios.put(`/api/videos/answer-keys/${this.editingAnswerKey.id}`, {
            content: this.answerKeyForm.content,
            sortOrder: this.answerKeyForm.sortOrder
          })
        } else {
          // Create
          await axios.post(`/api/videos/${this.currentVideoId}/answer-keys`, {
            content: this.answerKeyForm.content,
            sortOrder: this.answerKeyForm.sortOrder
          })
        }
        
        alert('保存成功！')
        this.closeAnswerKeyModal()
        await this.loadAnswerKeys(this.currentVideoId)
      } catch (error) {
        alert('保存失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.saving = false
      }
    },
    
    async deleteAnswerKey(videoId, answerKey) {
      if (!confirm('确定要删除这个标准答案吗？')) return
      
      try {
        await axios.delete(`/api/videos/answer-keys/${answerKey.id}`)
        alert('删除成功！')
        await this.loadAnswerKeys(videoId)
      } catch (error) {
        alert('删除失败：' + (error.response?.data?.message || error.message))
      }
    },
    
    goBack() {
      this.$router.push('/home')
    }
  }
}
</script>

<style scoped>
.page {
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  background: linear-gradient(135deg, #0c0c1d 0%, #1a1a2e 100%);
  min-height: 100vh;
  width: 100vw;
  padding: 20px;
  color: #fff;
  overflow-x: hidden;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 24px 30px;
  background: rgba(255,255,255,0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from { opacity:0; transform: translateY(-20px); }
  to { opacity:1; transform: translateY(0); }
}

.header-left h1 {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 2px;
  margin: 0;
}

.header-left h1 span {
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle {
  color: rgba(255,255,255,0.5);
  font-size: 14px;
  margin-top: 8px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.user-info {
  color: rgba(255,255,255,0.7);
  font-size: 14px;
}

.logout-btn {
  padding: 10px 24px;
  background: rgba(255,255,255,0.05);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.logout-btn:hover {
  background: rgba(255,255,255,0.1);
}

/* Actions Bar */
.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px 24px;
  background: rgba(255,255,255,0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
}

.actions-left {
  display: flex;
  gap: 15px;
  align-items: center;
}

.actions-right {
  color: rgba(255,255,255,0.5);
  font-size: 14px;
}

.select-control {
  padding: 12px 16px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  outline: none;
  cursor: pointer;
}

.select-control option {
  background: #1a1a2e;
  color: #fff;
}

/* Buttons */
.btn {
  padding: 12px 28px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-icon {
  font-size: 16px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102,126,234,0.4);
}

.btn-success {
  background: linear-gradient(135deg, #11998e, #38ef7d);
  color: white;
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(56,239,125,0.4);
}

.btn-outline {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
}

.btn-outline:hover {
  background: rgba(255,255,255,0.05);
}

.btn-sm {
  padding: 8px 16px;
  font-size: 13px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Form Card */
.form-card {
  margin-bottom: 30px;
  background: rgba(255,255,255,0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
  overflow: hidden;
}

.form-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.form-header h3 {
  margin: 0;
  font-size: 18px;
}

.form-body {
  padding: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 14px;
  color: rgba(255,255,255,0.7);
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.form-control {
  padding: 12px 16px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  transition: all 0.3s;
  outline: none;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.2);
  background: rgba(102,126,234,0.05);
}

.file-control {
  min-height: 44px;
  padding: 9px 12px;
}

.file-control::file-selector-button {
  margin-right: 12px;
  padding: 6px 12px;
  border: 0;
  border-radius: 6px;
  background: #667eea;
  color: #fff;
  cursor: pointer;
}

.textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* Video Grid */
.video-grid {
  min-height: 400px;
}

.video-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.video-card {
  background: rgba(255,255,255,0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
  overflow: hidden;
  transition: all 0.3s;
}

.video-card:hover {
  border-color: rgba(102,126,234,0.3);
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.3);
}

.video-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.video-type-badge {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.type-tutorial {
  background: rgba(0, 188, 212, 0.2);
  color: #26c6da;
}

.type-intro {
  background: rgba(102,126,234,0.2);
  color: #667eea;
}

.type-exam {
  background: rgba(56,239,125,0.2);
  color: #38ef7d;
}

.type-review {
  background: rgba(255,167,38,0.2);
  color: #ffa726;
}

.video-actions {
  display: flex;
  gap: 8px;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255,255,255,0.05);
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.icon-btn:hover {
  background: rgba(255,255,255,0.1);
  transform: scale(1.1);
}

.icon-btn.danger:hover {
  background: rgba(255,82,82,0.2);
}

.icon-btn.small {
  width: 28px;
  height: 28px;
  font-size: 14px;
}

.video-card-body {
  padding: 20px;
}

.video-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.video-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  gap: 8px;
  font-size: 13px;
}

.info-label {
  color: rgba(255,255,255,0.4);
}

.info-value {
  color: rgba(255,255,255,0.8);
}

.video-card-footer {
  padding: 16px 20px;
  border-top: 1px solid rgba(255,255,255,0.05);
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  background: rgba(102,126,234,0.3);
  color: #667eea;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
}

/* Answer Panel */
.answer-panel {
  padding: 20px;
  border-top: 1px solid rgba(255,255,255,0.05);
  background: rgba(0,0,0,0.2);
}

.answer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.answer-header h5 {
  margin: 0;
  font-size: 16px;
  color: rgba(255,255,255,0.9);
}

.answer-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.answer-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 16px;
  background: rgba(255,255,255,0.03);
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.05);
}

.answer-content {
  display: flex;
  gap: 12px;
  flex: 1;
}

.answer-index {
  color: #667eea;
  font-weight: 600;
  min-width: 24px;
}

.answer-text {
  color: rgba(255,255,255,0.8);
  line-height: 1.6;
  flex: 1;
}

.answer-actions {
  display: flex;
  gap: 8px;
  margin-left: 12px;
}

.empty-answer {
  text-align: center;
  padding: 20px;
  color: rgba(255,255,255,0.3);
  font-size: 14px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: linear-gradient(135deg, #1a1a2e, #16213e);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
  width: 90%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255,255,255,0.05);
  color: #fff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.close-btn:hover {
  background: rgba(255,255,255,0.1);
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid rgba(255,255,255,0.05);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: rgba(255,255,255,0.3);
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.empty-state h3 {
  font-size: 24px;
  color: rgba(255,255,255,0.5);
  margin: 0 0 12px 0;
}

.empty-state p {
  font-size: 16px;
  color: rgba(255,255,255,0.3);
}

/* Loading */
.loading-container {
  text-align: center;
  padding: 80px 20px;
}

.loading-small {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  color: rgba(255,255,255,0.5);
}

.spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid rgba(102,126,234,0.3);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-small {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Transitions */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter, .slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter, .slide-down-leave-to {
  opacity: 0;
  max-height: 0;
}

/* Stats */
.stats {
  color: rgba(255,255,255,0.5);
  font-size: 14px;
  padding: 8px 16px;
  background: rgba(255,255,255,0.03);
  border-radius: 8px;
}

/* Video Description */
.video-description {
  margin: 12px 0;
  padding: 12px 16px;
  background: rgba(102,126,234,0.05);
  border-left: 3px solid #667eea;
  border-radius: 4px;
}

.video-description p {
  margin: 0;
  color: rgba(255,255,255,0.7);
  font-size: 14px;
  line-height: 1.6;
}
</style>
